// Copyright 2021 The Prometheus Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import LRUCache from 'lru-cache';
import { labelMatchersToString } from '../parser';
import { Matcher } from '../types';
import { FetchFn } from '.';
import { PrometheusMetadataClient } from './metadata';

export interface MetricMetadata {
  type: string;
  help: string;
}

export interface PrometheusClient {
  labelNames(metricName?: string | null, matchers?: Matcher[]): Promise<string[]>;

  // labelValues return a list of the value associated to the given labelName.
  // In case a metric is provided, then the list of values is then associated to the couple <MetricName, LabelName>
  labelValues(labelName: string, metricName?: string, matchers?: Matcher[]): Promise<string[]>;

  metricMetadata(): Promise<Record<string, MetricMetadata[]>>;

  series(metricName: string, matchers?: Matcher[], labelName?: string): Promise<Array<Map<string, string>>>;

  // metricNames returns a list of suggestions for the metric name given the `prefix`.
  // Note that the returned list can be a superset of those suggestions for the prefix (i.e., including ones without the
  // prefix), as codemirror will filter these out when displaying suggestions to the user.
  metricNames(prefix?: string): Promise<string[]>;

  // flags returns flag values that prometheus was configured with.
  flags(): Promise<Record<string, string>>;

  // Optional metadata client that is exposed
  metadataClient?: PrometheusMetadataClient;
}

type TimeRange = {
  start: Date;
  end: Date;
};

export interface PrometheusConfig {
  url?: string;
  lookbackInterval?: number;
  timeRange?: TimeRange;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpErrorHandler?: (error: any) => void;
  fetchFn?: FetchFn;
  // Maximum number of successful network requests to memoize per client instance.
  requestCacheMaxEntries?: number;
  // How long a successful memoized network request remains reusable.
  requestCacheTtlMs?: number;
  httpMethod?: 'POST' | 'GET';
  apiPrefix?: string;
}

interface APIResponse<T> {
  status: 'success' | 'error';
  data?: T;
  error?: string;
  warnings?: string[];
}

// These are status codes where the Prometheus API still returns a valid JSON body,
// with an error encoded within the JSON.
const badRequest = 400;
const unprocessableEntity = 422;
const serviceUnavailable = 503;
const defaultRequestCacheMaxEntries = 300;
const defaultRequestCacheTtlMs = 5 * 60 * 1000;

// HTTPPrometheusClient is the HTTP client that should be used to get some information from the different endpoint provided by prometheus.
export class HTTPPrometheusClient implements PrometheusClient {
  private readonly lookbackInterval = 60 * 60 * 1000 * 12; //12 hours
  private readonly timeRange;
  private readonly url: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly errorHandler?: (error: any) => void;
  private readonly httpMethod: 'POST' | 'GET' = 'POST';
  private readonly apiPrefix: string = '/api/v1';
  // For some reason, just assigning via "= fetch" here does not end up executing fetch correctly
  // when calling it, thus the indirection via another function wrapper.
  private readonly fetchFn: FetchFn = (input: RequestInfo, init?: RequestInit): Promise<Response> => fetch(input, init);
  // Cache successful network requests in a bounded LRU so repeated calls can
  // reuse the same response without growing memory forever.
  private readonly requestCache: LRUCache<string, Promise<unknown>>;

  metadataClient: PrometheusMetadataClient;

  constructor(config: PrometheusConfig) {
    this.url = config.url ? config.url : '';
    this.errorHandler = config.httpErrorHandler;
    if (config.lookbackInterval) {
      this.lookbackInterval = config.lookbackInterval;
    }
    if (config.fetchFn) {
      this.fetchFn = config.fetchFn;
    }
    if (config.httpMethod) {
      this.httpMethod = config.httpMethod;
    }
    if (config.apiPrefix) {
      this.apiPrefix = config.apiPrefix;
    }
    if (config.timeRange) {
      this.timeRange = config.timeRange;
    }
    this.requestCache = new LRUCache<string, Promise<unknown>>({
      max: config.requestCacheMaxEntries ?? defaultRequestCacheMaxEntries,
      ttl: config.requestCacheTtlMs ?? defaultRequestCacheTtlMs,
    });

    this.metadataClient = new PrometheusMetadataClient({
      url: this.url,
    });
  }

  async labelNames(metricName?: string | null, matchers?: Matcher[]): Promise<string[]> {
    const { start, end } = this.getTimeRange();

    const searchParams = new URLSearchParams({
      start: start.toISOString(),
      end: end.toISOString(),
    });

    const matchersString = labelMatchersToString(metricName ?? '', matchers);
    if (matchersString) {
      searchParams.append('match[]', matchersString);
    }

    const request = this.buildRequest(this.labelsEndpoint(), searchParams);
    // See https://prometheus.io/docs/prometheus/latest/querying/api/#getting-label-names
    return this.fetchAPI<string[]>(request.uri, {
      method: this.httpMethod,
      body: request.body,
    }).catch((error) => {
      if (this.errorHandler) {
        this.errorHandler(error);
      }
      return [];
    });
  }

  // labelValues return a list of the value associated to the given labelName.
  // In case a metric is provided, then the list of values is then associated to the couple <MetricName, LabelName>
  labelValues(labelName: string, metricName?: string, matchers?: Matcher[]): Promise<string[]> {
    const { start, end } = this.getTimeRange();

    const params: URLSearchParams = new URLSearchParams({
      start: start.toISOString(),
      end: end.toISOString(),
    });

    const matchersString = labelMatchersToString(metricName ?? '', matchers);
    if (matchersString) {
      params.append('match[]', matchersString);
    }

    // See https://prometheus.io/docs/prometheus/latest/querying/api/#querying-label-values
    return this.fetchAPI<string[]>(`${this.labelValuesEndpoint().replace(/:name/gi, labelName)}?${params}`).catch(
      (error) => {
        if (this.errorHandler) {
          this.errorHandler(error);
        }
        return [];
      }
    );
  }

  metricMetadata(): Promise<Record<string, MetricMetadata[]>> {
    return this.fetchAPI<Record<string, MetricMetadata[]>>(this.metricMetadataEndpoint()).catch((error) => {
      if (this.errorHandler) {
        this.errorHandler(error);
      }
      return {};
    });
  }

  series(metricName: string, matchers?: Matcher[], labelName?: string): Promise<Array<Map<string, string>>> {
    const { start, end } = this.getTimeRange();
    const request = this.buildRequest(
      this.seriesEndpoint(),
      new URLSearchParams({
        start: start.toISOString(),
        end: end.toISOString(),
        'match[]': labelMatchersToString(metricName, matchers, labelName),
      })
    );
    // See https://prometheus.io/docs/prometheus/latest/querying/api/#finding-series-by-label-matchers
    return this.fetchAPI<Array<Map<string, string>>>(request.uri, {
      method: this.httpMethod,
      body: request.body,
    }).catch((error) => {
      if (this.errorHandler) {
        this.errorHandler(error);
      }
      return [];
    });
  }

  metricNames(): Promise<string[]> {
    return this.labelValues('__name__');
  }

  flags(): Promise<Record<string, string>> {
    return this.fetchAPI<Record<string, string>>(this.flagsEndpoint()).catch((error) => {
      if (this.errorHandler) {
        this.errorHandler(error);
      }
      return {};
    });
  }

  getMetricType(metricName: string) {
    return this.metadataClient.getMetricType(metricName);
  }

  getIsClassicHistogramMetric(metricName: string) {
    return this.metadataClient.getIsClassicHistogramMetric(metricName);
  }

  private fetchAPI<T>(resource: string, init?: RequestInit): Promise<T> {
    const requestKey = this.buildRequestKey(resource, init);
    const cachedRequest = this.requestCache.get(requestKey);
    if (cachedRequest) {
      // This request was already sent successfully or is still in flight.
      // Reuse it so we do not hit the network twice for the same call.
      return cachedRequest as Promise<T>;
    }

    const request = this.fetchFn(this.url + resource, init)
      .then((res) => {
        if (!res.ok && ![badRequest, unprocessableEntity, serviceUnavailable].includes(res.status)) {
          throw new Error(res.statusText);
        }
        return res;
      })
      .then((res) => res.json())
      .then((apiRes: APIResponse<T>) => {
        if (apiRes.status === 'error') {
          throw new Error(apiRes.error !== undefined ? apiRes.error : 'missing "error" field in response JSON');
        }
        if (apiRes.data === undefined) {
          throw new Error('missing "data" field in response JSON');
        }
        return apiRes.data;
      })
      .catch((error) => {
        // Only keep successful results cached. Failures should be retried on the next call.
        this.requestCache.delete(requestKey);
        throw error;
      });

    // Store the Promise immediately so duplicate callers share the same fetch and response.
    this.requestCache.set(requestKey, request);
    return request;
  }

  private buildRequestKey(resource: string, init?: RequestInit): string {
    // Include the request shape in the cache key so GET/POST variants and
    // different params or headers do not accidentally share a cached result.
    return JSON.stringify({
      resource,
      method: init?.method ?? 'GET',
      body: this.buildRequestBodyKey(init?.body),
      headers: this.buildRequestHeadersKey(init?.headers),
    });
  }

  private buildRequestBodyKey(body?: RequestInit['body']): string {
    if (body === undefined || body === null) {
      return '';
    }
    if (typeof body === 'string') {
      return body;
    }
    if (body instanceof URLSearchParams) {
      return body.toString();
    }
    return String(body);
  }

  private buildRequestHeadersKey(headersInit?: HeadersInit): string {
    if (!headersInit) {
      return '';
    }

    const headers: Array<[string, string]> = [];
    new Headers(headersInit).forEach((value, key) => {
      headers.push([key, value]);
    });

    headers.sort(([left], [right]) => left.localeCompare(right));
    return JSON.stringify(headers);
  }

  private buildRequest(endpoint: string, params: URLSearchParams) {
    let uri = endpoint;
    let body: URLSearchParams | null = params;
    if (this.httpMethod === 'GET') {
      uri = `${uri}?${params}`;
      body = null;
    }
    return { uri, body };
  }

  private labelsEndpoint(): string {
    return `${this.apiPrefix}/labels`;
  }

  private labelValuesEndpoint(): string {
    return `${this.apiPrefix}/label/:name/values`;
  }

  private seriesEndpoint(): string {
    return `${this.apiPrefix}/series`;
  }

  private metricMetadataEndpoint(): string {
    return `${this.apiPrefix}/metadata`;
  }

  private flagsEndpoint(): string {
    return `${this.apiPrefix}/status/flags`;
  }

  private getTimeRange(): TimeRange {
    // If the time range is set, use it otherwise use the lookback interval
    if (this.timeRange) {
      return this.timeRange;
    }
    const end = new Date();
    const start = new Date(end.getTime() - this.lookbackInterval);
    return { start, end };
  }
}
